import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { IndustryCategory, BusinessSubcategory, DiscoveryState } from '@/types/discovery';

interface DiscoveryContextValue {
  state: DiscoveryState;
  selectIndustry: (industry: IndustryCategory) => void;
  selectSubcategory: (subcategory: BusinessSubcategory) => void;
  goBack: () => void;
  reset: () => void;
  canGoBack: boolean;
}

const initialState: DiscoveryState = {
  industry: null,
  subcategory: null,
  step: 'industry',
};

const DiscoveryContext = createContext<DiscoveryContextValue | null>(null);

export const useDiscovery = () => {
  const context = useContext(DiscoveryContext);
  if (!context) {
    throw new Error('useDiscovery must be used within a DiscoveryProvider');
  }
  return context;
};

interface DiscoveryProviderProps {
  children: ReactNode;
  initialIndustry?: IndustryCategory;
  initialSubcategory?: BusinessSubcategory;
}

export const DiscoveryProvider = ({
  children,
  initialIndustry,
  initialSubcategory,
}: DiscoveryProviderProps) => {
  const [state, setState] = useState<DiscoveryState>(() => {
    if (initialSubcategory && initialIndustry) {
      return {
        industry: initialIndustry,
        subcategory: initialSubcategory,
        step: 'genies',
      };
    }
    if (initialIndustry) {
      return {
        industry: initialIndustry,
        subcategory: null,
        step: 'subcategory',
      };
    }
    return initialState;
  });

  const selectIndustry = useCallback((industry: IndustryCategory) => {
    setState({
      industry,
      subcategory: null,
      step: 'subcategory',
    });
  }, []);

  const selectSubcategory = useCallback((subcategory: BusinessSubcategory) => {
    setState(prev => ({
      ...prev,
      subcategory,
      step: 'genies',
    }));
  }, []);

  const goBack = useCallback(() => {
    setState(prev => {
      if (prev.step === 'genies') {
        return { ...prev, subcategory: null, step: 'subcategory' };
      }
      if (prev.step === 'subcategory') {
        return { industry: null, subcategory: null, step: 'industry' };
      }
      return prev;
    });
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const canGoBack = state.step !== 'industry';

  return (
    <DiscoveryContext.Provider
      value={{
        state,
        selectIndustry,
        selectSubcategory,
        goBack,
        reset,
        canGoBack,
      }}
    >
      {children}
    </DiscoveryContext.Provider>
  );
};
